import {Flex,Grid,GridItem,Box,Select,InputGroup,Button,
    InputLeftElement,Input,Heading,Text,TableContainer,
    Checkbox,Table,Thead,Tbody,Th,Textarea,Tr,Td,} from '@chakra-ui/react';
    import styled from "@emotion/styled";
    import { LuSearch as SearchIcon } from "react-icons/lu";
    import { useState,useEffect } from 'react';
    import {useAtom,atom} from 'jotai'
    import { useMutation, useQuery, useQueryClient } from 'react-query';
    import apiCall from '../../../../../utils/apiCall';
    import {  openAtom, notificationAtom, openNAtom, modalAtom } from "../../../../../context";
  import { Loader } from '../../../../../customs';
  import { TbReload as ReloadIcon } from "react-icons/tb";
  import ReactPaginate from 'react-paginate';
  
  
  const indexAtom = atom<number | null>(null);
  
    const HeadStyle={
      borderBottom:"1px solid var(--dark-green-color)",
      padding:'15px 5px',
    }
    const DataStyle = {
      padding:'5px 5px',
      thcolor:'var(--dark-green-color)'
    }
    
    
    const ROWSTYLE = styled.tr`
      & > td:last-of-type{
        width:50px;
        height:63px;
        & > div{
          display:none;
        }
      }
      &:hover > *{
        cursor: pointer;
        background-color:var(--table-highlight);
      } 
      & > td{
        width:140px;
      }
    `
  
export default function BatchMigration() {
  // basic UI/query hooks
  const [hide, set_hide] = useState<boolean>(true);
  const [activeIndex] = useAtom(indexAtom);
  const [, set_open] = useAtom(openAtom);
  const [, set_n_open] = useAtom(openNAtom);
  const [, set_notification] = useAtom(notificationAtom);
  const queryClient = useQueryClient();
  const [, set_modal] = useAtom(modalAtom);

  // local state
  const [search, set_search] = useState<string>("");
  const [selected_status, set_selected_status] = useState<string | null>(null);

  // checkboxes
  const [is_check_all, set_is_check_all] = useState<boolean>(false);
  const [checked_ids, set_checked_ids] = useState<string[]>([]);

  // pagination (backend)
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // server-side fetch: uses PageNumber and PageSize
  const fetchKey = [
    "applied-induction",
    pageNumber,
    pageSize,
    selected_status,
    search,
  ];

  const {
    data: apiResponse,
    isLoading,
    refetch,
  } = useQuery(
    fetchKey,
    async () => {
      const params: Record<string, string | number> = {
        PageNumber: pageNumber,
        PageSize: pageSize,
      };
      if (selected_status) params.Status = selected_status;
      if (search) params.search = search;

      const res = await apiCall().get(
        `/Exam/students/appliedForInduction/notMovedToMembership`,
        { params }
      );
      // expected shape:
      // { reportItems: [...], PageNumber, PageSize, TotalCount, PageCount, message }
      return res.data;
    },
    {
      keepPreviousData: true,
      staleTime: 30_000,
    }
  );

  // derive safe values
  const reportItems: any[] = Array.isArray(apiResponse?.reportItems)
    ? apiResponse!.reportItems
    : [];
  const totalCount: number = Number(apiResponse?.TotalCount ?? 0);
  const pageCount: number =
    apiResponse?.PageCount ?? Math.ceil((totalCount || 0) / pageSize);

  // Keep a local copy of current page items for UI actions (select/check, search client fallback)
  const [currentPageItems, set_currentPageItems] = useState<any[]>([]);

  useEffect(() => {
    set_currentPageItems(reportItems);
    // clear selection when page changes or items change
    set_checked_ids([]);
    set_is_check_all(false);
  }, [reportItems, pageNumber, selected_status, search]);

  // debounced search (server-side)
  useEffect(() => {
    const t = setTimeout(() => {
      setPageNumber(1); // reset to first page when search changes
      refetch();
    }, 400);
    return () => clearTimeout(t);
  }, [search, selected_status, pageSize]);

  // checkbox handlers — operate on current page items (reportItems)
  const handleMultipleCheck = () => {
    if (!is_check_all) {
      const pageStudentNos = currentPageItems
        .map((s: any) => s.StudentNo)
        .filter(Boolean);
      set_checked_ids(pageStudentNos);
      set_is_check_all(true);
    } else {
      set_checked_ids([]);
      set_is_check_all(false);
    }
  };

  const handleSingleCheck = (studentNo: string) => {
    if (checked_ids.includes(studentNo)) {
      set_checked_ids((prev) => prev.filter((id) => id !== studentNo));
      set_is_check_all(false);
    } else {
      set_checked_ids((prev) => [...prev, studentNo]);
      // if after adding we cover all current page items, mark check-all
      const allOnPage = currentPageItems.map((i: any) => i.StudentNo).filter(Boolean);
      const newChecked = [...checked_ids, studentNo];
      if (allOnPage.every((sNo) => newChecked.includes(sNo))) {
        set_is_check_all(true);
      }
    }
  };

  const handleCheck = (studentNo: string) => checked_ids.includes(studentNo);

  // safe text-search on server side input box
  const onSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    set_search(e.target.value);
  };

  // mutation: move selected students to membership
  const { mutate: update_move_membership, isLoading: moveIsLoading } =
    useMutation(
      async () => {
        if (!checked_ids || checked_ids.length === 0) {
          throw new Error("No students selected");
        }
        // correct URL: ?studentNumbers=...&studentNumbers=...
        const query = checked_ids
          .map((id) => `studentNumbers=${encodeURIComponent(id)}`)
          .join("&");
        const url = `/Exam/BatchMoveToMembership?${query}`;
        return await apiCall().post(url, {});
      },
      {
        onSuccess: (res: any) => {
          set_notification({
            type: "success",
            message: res?.data?.Message || "Students moved to membership",
          });
          set_n_open(true);
          queryClient.invalidateQueries(["applied-induction"]);
          // refresh current page
          refetch();
          // clear selection
          set_checked_ids([]);
          set_is_check_all(false);
        },
        onError: (err: any) => {
          set_notification({
            type: "error",
            message:
              err?.response?.data?.Message ||
              err?.message ||
              "Error moving students",
          });
          set_n_open(true);
        },
        onSettled: () => set_open(false),
      }
    );

  // handle page change (ReactPaginate -> gives selected index)
  const handlePageClick = (event: { selected: number }) => {
    const selected = event.selected + 1; // API page numbers are 1-based
    if (selected === pageNumber) return;
    setPageNumber(selected);
  };

  // status options (you confirmed these values)
  const statusOptions = [
    "Applied for Induction",
    "Not Applied",
    "Inducted",
    "Moved",
  ];

  // small helpers for safe display strings
  const startIndex = totalCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
  const endIndex = Math.min(pageNumber * pageSize, totalCount);

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <Grid minW="80dvw" p="20px" templateColumns="1fr">
          {/* controls */}
          <GridItem as={Flex} width="100%" align="center" mb={4}>
          {/* Search Input */}
          {/* <InputGroup
            h="40px"
            width="250px"
            bg="var(--white-color)"
            borderColor="var(--dark-green-color)"
          >
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              value={search}
              onChange={onSearchInput}
              placeholder="Search (studentNo or name)"
            />
            <span
              style={{
                position: "absolute",
                zIndex: 1,
                right: "10px",
                cursor: "pointer",
                top: "12px",
              }}
              onClick={() => {
                set_search("");
                setPageNumber(1);
                refetch();
              }}
            >
              <ReloadIcon />
            </span>
          </InputGroup> */}

          {/* Status Filter */}
          <Box ml="auto">
            <Select
              w="220px"
              placeholder="Filter by Status"
              value={selected_status ?? ""}
              onChange={(e) => {
                const v = e.target.value || null;
                set_selected_status(v);
                setPageNumber(1);
              }}
            >
              <option value="">All Status</option>
              {statusOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </Box>
        </GridItem>


          {/* table */}
          <GridItem as={Flex} flexDir="column" gap="20px">
            <Heading>Applied For Induction</Heading>
            <Text>
              Showing {startIndex}–{endIndex} of {totalCount}
            </Text>

            {reportItems && reportItems.length > 0 ? (
              <TableContainer mt="20px">
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th sx={{ ...HeadStyle, maxWidth: "50px" }}>
                        <label>
                          <Checkbox
                            isChecked={is_check_all}
                            onChange={handleMultipleCheck}
                            colorScheme="green"
                          />
                        </label>
                      </Th>
                      <Th sx={HeadStyle}>S/N</Th>
                      <Th sx={HeadStyle}>Student No</Th>
                      <Th sx={HeadStyle}>First Name</Th>
                      <Th sx={HeadStyle}>Last Name</Th>
                      <Th sx={HeadStyle}>IsMoved?</Th>
                      <Th sx={HeadStyle}>IsOld?</Th>
                      <Th sx={HeadStyle}>Status</Th>
                    </Tr>
                  </Thead>

                  <Tbody>
                    {reportItems.map((eCandidate: any, idx: number) => {
                      const key = eCandidate.StudentNo ?? `${pageNumber}-${idx}`;
                      return (
                        <ROWSTYLE key={key}>
                          <Td sx={{ ...DataStyle, maxWidth: "50px" }}>
                            <Checkbox
                              colorScheme={
                                handleCheck(eCandidate.StudentNo)
                                  ? "green"
                                  : undefined
                              }
                              isChecked={handleCheck(eCandidate.StudentNo)}
                              onChange={() =>
                                handleSingleCheck(eCandidate.StudentNo)
                              }
                            />
                          </Td>

                          <Td sx={DataStyle}>{eCandidate.SN}</Td>
                          <Td sx={DataStyle}>{eCandidate.StudentNo}</Td>
                          <Td sx={DataStyle}>{eCandidate.FirstName}</Td>
                          <Td sx={DataStyle}>{eCandidate.LastName}</Td>
                          <Td sx={DataStyle}>
                            {eCandidate.IsMovedToMembership ? "Yes" : "No"}
                          </Td>
                          <Td sx={DataStyle}>
                            {eCandidate.IsOldStudent ? "Yes" : "No"}
                          </Td>
                          <Td sx={DataStyle}>{eCandidate.Status}</Td>
                        </ROWSTYLE>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            ) : (
              <Text mt="20px">No Students</Text>
            )}

            {/* pagination */}
            <StylePaginateBox>
              <ReactPaginate
                breakLabel="..."
                nextLabel=">"
                onPageChange={handlePageClick}
                pageRangeDisplayed={5}
                pageCount={pageCount || 1}
                forcePage={Math.max(0, pageNumber - 1)}
                previousLabel="<"
                renderOnZeroPageCount={null}
              />
            </StylePaginateBox>
          </GridItem>

          {/* action */}
          <GridItem as={Flex} flexDir="column" gap="20px">
            <Flex justify="center" gap="30px" align="center">
              <Button
                bg="var(--dark-green-color)"
                color="var(--white-color)"
                width="130px"
                margin="20px"
                onClick={() => {
                  if (checked_ids.length === 0) {
                    set_notification({
                      type: "error",
                      message: "Please select at least one student",
                    });
                    set_n_open(true);
                    return;
                  }
                  update_move_membership();
                }}
                isDisabled={moveIsLoading}
              >
                {moveIsLoading ? "Moving..." : "Move"}
              </Button>
            </Flex>
          </GridItem>
        </Grid>
      )}
    </>
  );
}


    const StylePaginateBox = styled.div`
    width:100%;
    & > ul{
      list-style-type: none;
      text-align:center;
      padding:0;
      margin:0;
      margin-top:10px;
    } 
    & > ul > li{
      display: inline;
      margin:0 5px;
      padding:2px 5px;
      border-radius: 3px;
      background-color: transparent;
      color:var(--dark-green-color);
    }
    & > ul > li.selected{
      background-color: var(--highlight-paginate-background);
      color:var(--highlight-paginate-color);
    }
    & > ul > li:first-of-type,
    & > ul > li:last-of-type{
      font-weight: bold;
    }
  `